import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Disc3 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double-processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      console.log("Auth callback - processing hash:", hash);
      console.log("Auth callback - API URL:", API);
      
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error("No session_id found in URL");
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log("Auth callback - session_id found:", sessionId.substring(0, 10) + "...");

      try {
        // Exchange session_id for session_token
        console.log("Auth callback - calling:", `${API}/auth/session`);
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        console.log("Auth callback - session response status:", response.status);

        // Read body once as text to avoid "Body is disturbed or locked" errors
        const responseText = await response.text();

        if (!response.ok) {
          console.error("Auth session error:", responseText);
          throw new Error("Failed to authenticate");
        }

        const userData = JSON.parse(responseText);
        console.log("Auth callback - user authenticated:", userData.email);
        console.log("Auth callback - session_token received:", !!userData.session_token);
        
        // Store session token for Safari/cross-domain fallback
        if (userData.session_token) {
          localStorage.setItem("session_token", userData.session_token);
          login(userData, userData.session_token);
        } else {
          login(userData);
        }
        
        // Clean URL and redirect to dashboard
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Auth error:", error);
        alert("Login failed: " + error.message + "\n\nPlease try again or contact support.");
        navigate("/");
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background" data-testid="auth-callback">
      <Disc3 className="w-16 h-16 text-primary animate-spin mb-4" />
      <p className="text-muted-foreground">Signing you in...</p>
    </div>
  );
}
